import { HTMLAttributes, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/password-input'
import { useAuthStore } from '@/stores/authStore'
import { login } from '@/requests/auth'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/use-translation'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Please enter your email' })
    .email({ message: 'Invalid email address' }),
  password: z
    .string()
    .min(1, {
      message: 'Please enter your password',
    })
    .min(4, {
      message: 'Password must be at least 7 characters long',
    }),
})

export function UserAuthForm({ className, ...props }: UserAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  async function onSubmit(data: z.infer<typeof formSchema>) {
    try {
      setIsLoading(true)

      // Make API call to your authentication endpoint
      const response = await login(data)

      // Get the auth store actions
      const { setUser, setAccessToken } = useAuthStore.getState().auth

      // Update the auth store with user data and token
      setUser({
        email: response.user.email,
        name: response.user.name,
        id: response.user.id,
        last_name: response.user.last_name,
        role_id: response.user.role_id,
        organization_id: response.user.organization_id,
        role: response.organization && response.organization.plan,
      })

      // Set the access token
      setAccessToken(response.token)

      // Navigate to dashboard or intended destination
      const searchParams = new URLSearchParams(window.location.search)
      let redirect = searchParams.get('redirect') || '/'

      // Clean up nested redirect parameters and validate the redirect URL
      try {
        // Decode the redirect parameter in case it's URL encoded
        redirect = decodeURIComponent(redirect)

        // If redirect contains another redirect parameter, extract the final destination
        if (redirect.includes('redirect=')) {
          const redirectUrl = new URL(redirect, window.location.origin)
          const nestedRedirect = redirectUrl.searchParams.get('redirect')
          if (nestedRedirect) {
            redirect = decodeURIComponent(nestedRedirect)
          }
        }

        // Ensure redirect is a valid path and not an external URL
        if (!redirect.startsWith('/') || redirect.startsWith('//')) {
          redirect = '/'
        }
      } catch (error) {
        // If there's any error in parsing, default to home
        redirect = '/'
      }

      navigate({ to: redirect })
    } catch (error: any) {
      // Handle login errors
      let errorMessage = 'Login failed. Please try again.'
      
      // Check for specific error responses
      if (error?.response?.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.'
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      toast({
        title: 'Authentication Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder='name@example.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <div className='flex items-center justify-between'>
                    <FormLabel>{t('Password')}</FormLabel>
                    <Link
                      to='/forgot-password'
                      className='text-sm font-medium text-muted-foreground hover:opacity-75'
                    >
                      {t('Forgot password?')}
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput placeholder='********' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className='mt-2' disabled={isLoading}>
              Login
            </Button>

          </div>
        </form>
      </Form>
    </div>
  )
}
